import { Divider } from "@mui/material";
import React from "react";
import Article from "../../components/Article";
import ArticleImage from "../../components/ArticleImage";
import TextLink from "../../components/TextLink";
import agenticChatImage from "../../assets/images/appsync-bedrock-agentic-chat.png";

export const AppsyncBedrockAgenticChatDetails = {
  articleTitle: "Agentic chat on AppSync and Bedrock",
  articleDate: "2026-06-18",
  articleAuthor: "Harrison Cannon",
  articleImage: agenticChatImage,
  articleBody: "How I built a streaming agentic chatbot with AppSync, Bedrock ConverseStream, DynamoDB tool lookups, and an async Lambda orchestrator.",
  articleSlug: "appsync-bedrock-agentic-chat"
};

function AppsyncBedrockAgenticChat() {
  function ArticleContent() {
    return (
      <div>
        <h2>Background</h2>

        <p>
          I wanted a chat experience that felt live, stayed grounded in the user&apos;s own data, and did not block the GraphQL request while a foundation model thought. The build lives across <code>infra</code> for AWS CDK and <code>app</code> for the mobile client. The product surface is a normal agentic chatbot: sessions, messages, streaming tokens, and read-only tools that look things up in DynamoDB.
        </p>

        <p>
          The hard part was not &quot;call a model.&quot; It was keeping AppSync mutations fast, streaming partial replies to a phone, and letting the model fetch user-scoped records without inventing them.
        </p>

        <ArticleImage
          image={agenticChatImage}
          caption="Architecture for the AppSync and Bedrock agentic chat path"
        />

        <Divider className="divider" />

        <h2>Design constraints</h2>

        <p>
          Before wiring Bedrock, I locked the constraints:
        </p>

        <ul>
          <li>Mutations must return quickly. Model time and tool rounds cannot sit inside the AppSync request budget.</li>
          <li>Replies must stream to the client as tokens arrive, then land as durable messages.</li>
          <li>Tools are read-only lookups against DynamoDB for the authenticated user only.</li>
          <li>Usage needs a daily limit that survives retries and concurrent sends.</li>
          <li>History, sessions, and rate counters live in the same single-table design as the rest of the API.</li>
        </ul>

        <p>
          That led to Cognito for identity, AppSync GraphQL for control-plane operations, a thin ingress Lambda, an async orchestrator Lambda on Bedrock <code>ConverseStream</code>, DynamoDB for state and tools, and a separate AppSync Events API for the token stream.
        </p>

        <Divider className="divider" />

        <h2>Architecture overview</h2>

        <p>
          Everything for this path runs in <code>eu-west-1</code>. The client authenticates with Cognito, then uses the same user pool for GraphQL and Events.
        </p>

        <ol>
          <li>Create or list sessions through AppSync resolvers backed by DynamoDB.</li>
          <li>Send a message with a Cognito-protected mutation. AppSync invokes <code>SendMessage</code>.</li>
          <li><code>SendMessage</code> reserves daily usage, writes the user message, async-invokes <code>Orchestrator</code>, and returns channel metadata immediately.</li>
          <li>The client subscribes to an AppSync Events channel scoped to that user and session.</li>
          <li><code>Orchestrator</code> loads history, runs Bedrock with tools, publishes stream events, and persists the assistant message.</li>
        </ol>

        <p>
          Splitting ingress from orchestration was the main structural choice. GraphQL stays a request/response API. The model turn is an event-driven job that happens to stream side effects back over Events.
        </p>

        <Divider className="divider" />

        <h2>Fast mutation, async agent turn</h2>

        <p>
          <code>SendMessage</code> is intentionally small. It validates the session, rejects oversized content, and uses a conditional DynamoDB write to reserve one unit of daily usage. If the reserve fails, the client gets a rate-limit error before any Bedrock spend.
        </p>

        <p>
          After the user message is stored, the function invokes the orchestrator with <code>InvocationType: Event</code>. The mutation response includes the session id, message ids, and the Events channel path. The UI can paint the user bubble and open the stream without waiting for the model.
        </p>

        <p>
          That pattern also keeps AppSync timeouts out of the agent loop. Soft timeouts and max tool iterations live on the orchestrator, where a 120-second Lambda budget is enough for a few tool rounds.
        </p>

        <Divider className="divider" />

        <h2>Why not a plain LLM, and why not RAG</h2>

        <p>
          An off-the-shelf chat model is a poor product chatbot on its own. It has no access to the signed-in user&apos;s private records, so it guesses. It will invent preferences, invent recent activity, and sound confident while being wrong. You also cannot enforce tenancy in the model weights: nothing stops a prompt from asking about another user if the only defence is &quot;please don&apos;t.&quot; Daily limits, auditability, and &quot;answer from what we actually stored&quot; all have to live outside the model.
        </p>

        <p>
          Retrieval-augmented generation (RAG) is the usual next idea. You embed documents into a vector index, retrieve the chunks most similar to the user&apos;s question, and stuff those chunks into the prompt so the model grounds itself in retrieved text. RAG fits knowledge bases, help centres, and long unstructured docs where &quot;find something roughly about this&quot; is the access pattern.
        </p>

        <p>
          It is a weak fit here. The useful context is structured, per-user DynamoDB data: profile, preferences, and recent records keyed by Cognito <code>sub</code>. That data changes as the user uses the product, so a vector index would be a lagging shadow copy you have to keep re-embedding. Similarity search is the wrong primitive when you need an exact read of the latest items for one user. Mixing every user&apos;s embeddings in one corpus also makes isolation harder than a query that cannot see another partition key.
        </p>

        <p>
          Tool calling against DynamoDB is what this setup is for. The model decides it needs data, Lambda runs a narrow read with the authenticated <code>sub</code> injected by the server, and the next Bedrock turn sees live product state. That enables grounded replies without a separate search index, keeps tenancy in IAM and key design instead of prompt etiquette, and lets the chatbot use the same source of truth as the rest of the API.
        </p>

        <p>
          The important part is physical impossibility, not politeness. If a user asks the chatbot to read someone else&apos;s profile or history, there is no tool path that can do it. The orchestrator never takes a target user id from the model or the prompt. Every DynamoDB query is built with the caller&apos;s Cognito <code>sub</code> from the authenticated request, so the partition the tools can see is only that user&apos;s. Jailbreak text cannot widen the keyspace. The model might still <em>claim</em> it looked something up; it cannot actually fetch another tenant&apos;s items.
        </p>

        <Divider className="divider" />

        <h2>Bedrock tool loop against DynamoDB</h2>

        <p>
          The orchestrator uses Bedrock <code>ConverseStream</code> with a declared tool config. When the model asks for a tool, Lambda executes it in-process and feeds the result back into the next converse turn. There is no separate agent runtime service. The loop is plain TypeScript with caps on iterations and wall-clock time.
        </p>

        <p>
          Tools are narrow and user-scoped. Typical ones look like:
        </p>

        <ul>
          <li><code>getUserProfile</code> and <code>getUserPreferences</code> for account context</li>
          <li>lookups for recent records the user already stored in DynamoDB</li>
          <li>detail fetches by id after a list tool returns candidates</li>
        </ul>

        <p>
          Every tool receives the Cognito <code>sub</code> from the orchestrator event, not from model-supplied arguments. A prompt that says &quot;load user B&apos;s records&quot; still runs as user A&apos;s keys. The same single-table keys the GraphQL API already uses power these reads, so the chatbot sees the live product data rather than a shadow copy. Same modelling idea as my earlier <TextLink url="/blog/appsync-dynamodb-single-table-design" text="AppSync and DynamoDB single-table post" />.
        </p>

        <p>
          Sessions and messages use keys such as:
        </p>

        <table>
          <thead>
            <tr>
              <th>PK</th>
              <th>SK</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><code>USER#&lt;sub&gt;</code></td>
              <td><code>CHAT_SESSION#&lt;sessionId&gt;</code></td>
            </tr>
            <tr>
              <td><code>USER#&lt;sub&gt;</code></td>
              <td><code>CHAT#&lt;sessionId&gt;#MSG#&lt;createdAt&gt;#&lt;messageId&gt;</code></td>
            </tr>
            <tr>
              <td><code>USER#&lt;sub&gt;</code></td>
              <td><code>CHAT_USAGE#&lt;yyyy-mm-dd&gt;</code></td>
            </tr>
          </tbody>
        </table>

        <p>
          History is windowed before it goes to Bedrock so long sessions do not blow the context window. The final assistant text is written once the stream completes, with optional structured output parsed from a fenced block when a mode needs machine-readable content.
        </p>

        <Divider className="divider" />

        <h2>AppSync Events for streaming</h2>

        <p>
          GraphQL subscriptions were possible, but AppSync Events fitted the publish path better. The orchestrator signs HTTP publishes with IAM. Clients connect and subscribe with Cognito. Publish is never allowed from the user pool.
        </p>

        <p>
          Channel handlers enforce a path shape of <code>/chat/&#123;userId&#125;/&#123;sessionId&#125;</code> and require the Cognito <code>sub</code> to match the path. A user can only listen to their own sessions. Stream events cover tokens, tool activity, completion, and client-safe errors so the UI can degrade without exposing internal failures.
        </p>

        <p>
          Publishing is best-effort relative to persistence. If a publish batch fails, the orchestrator still tries to finish and store the assistant message. The client can always reload history from GraphQL if the socket drops mid-turn.
        </p>

        <Divider className="divider" />

        <h2>Client responsibilities</h2>

        <p>
          In <code>app</code>, the chat hook owns three concerns: session CRUD over Amplify GraphQL, opening the Events channel before or immediately after send, and merging streamed tokens into a local message list. When the turn completes, the streamed draft is replaced by the durable message shape, including any structured payload the UI knows how to render.
        </p>

        <p>
          That split keeps the mobile UI responsive. Users see their own message immediately, then tokens arrive on the Events channel while Bedrock and DynamoDB tools run in the background.
        </p>

        <Divider className="divider" />

        <h2>Infrastructure as code</h2>

        <p>
          <code>infra</code> owns the GraphQL types, pipeline resolvers, both Lambdas, IAM for Bedrock invoke and Events publish, and the Events API custom domain. Session deletes fan into cleanup through the existing DynamoDB stream and EventBridge pipe pattern so message items do not linger after a session is removed.
        </p>

        <p>
          Bedrock access is locked to the inference profiles the orchestrator is allowed to call. The Events namespace grants publish only to the orchestrator role. Cognito authorizes GraphQL mutations and Event subscriptions. There is no long-lived model key in the mobile app.
        </p>

        <Divider className="divider" />

        <h2>What mattered in practice</h2>

        <ul>
          <li>Async invoke plus Events streaming beat trying to hold an AppSync resolver open for the whole agent turn.</li>
          <li>Conditional usage writes were simpler and safer than counting completed replies after the fact.</li>
          <li>User-scoped tools with server-injected identity mattered more than clever prompts for keeping answers honest.</li>
          <li>Soft timeouts and max tool iterations made failure modes predictable on mobile networks.</li>
          <li>History reload from DynamoDB covered the cases where the websocket blipped.</li>
        </ul>

        <Divider className="divider" />

        <h2>Final thoughts</h2>

        <p>
          The chatbot is still &quot;just&quot; Cognito, AppSync, Lambda, Bedrock, and DynamoDB. The interesting shape is the boundary: GraphQL accepts work, Events delivers the live experience, and tools read the same single-table data the rest of the API already trusts. That kept the agentic path aligned with the existing serverless backend instead of becoming a separate AI sidecar.
        </p>
      </div>
    );
  }

  return <Article
    article={AppsyncBedrockAgenticChatDetails}
    content={ArticleContent}
  />;
}

export default AppsyncBedrockAgenticChat;
